import { cn } from "@/lib/utils";

import { TableRow } from "@/components/ui/table";
import { Cell, Row } from "@tanstack/react-table";
import { VirtualItem } from "@tanstack/react-virtual";
import { memo, useCallback, useMemo } from "react";
import PeriodsTableCell from "./PeriodsTableCell";
import { useRowSelectionCell } from "@/components/data-table/hooks/useRowSelectionCell";

interface PeriodsTableRowProps<TData extends { id: string }> {
  row: Row<TData>;
  onRowMouseEnterInternal: (id: string) => () => void;
  onRowMouseLeaveInternal: (id: string) => () => void;
  virtualRow: VirtualItem;
  isBusy: boolean;
  cells: Cell<TData, unknown>[];
}

function PeriodsTableRow<TData extends { id: string }>({
  row,
  isBusy,
  onRowMouseEnterInternal,
  onRowMouseLeaveInternal,
  virtualRow,
  cells,
}: PeriodsTableRowProps<TData>) {
  const { isSelected } = useRowSelectionCell(row.original.id);
  const style = useMemo(
    () => ({
      transform: `var(--row-transform-${virtualRow.index})`,
      backgroundColor: `var(--row-bg-${row.id})`,
    }),
    [virtualRow.index, row.id],
  );

  const onMouseEnter = useCallback(() => {
    onRowMouseEnterInternal(row.id)();
  }, [onRowMouseEnterInternal, row.id]);

  const onMouseLeave = useCallback(() => {
    onRowMouseLeaveInternal(row.id)();
  }, [onRowMouseLeaveInternal, row.id]);

  return (
    <TableRow
      className={cn("absolute p-0 flex w-full", {
        "bg-blue-200/40 hover:bg-blue-200/80": isSelected,
      })}
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-index={virtualRow.index}
    >
      {cells.map((cell) => {
        return <PeriodsTableCell key={cell.id} cell={cell} isBusy={isBusy} />;
      })}
    </TableRow>
  );
}

export default memo(PeriodsTableRow) as <TData extends { id: string }>(
  props: PeriodsTableRowProps<TData>,
) => JSX.Element;
